import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Apis } from "../../lib/Apis";
import { ArrowLeft } from "lucide-react";

function formatMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? `Rs. ${n.toLocaleString()}` : String(v);
}

export default function PurchasedHouseDesignDetail() {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!purchaseId) return;
      setLoading(true);
      try {
        const response = await Apis.getPurchasedHouseDesignById(purchaseId);
        if (!cancelled) setDetail(response.data.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          Swal.fire({
            icon: "error",
            title: "Could not load",
            text: "Purchase not found or you may not have access.",
          });
          navigate("/purchased-house-designs");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [purchaseId, navigate]);

  if (loading || !detail) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Loading purchase details…</p>
      </div>
    );
  }

  const user = detail.user || {};
  const hd = detail.house_design || {};
  const info = detail.user_information || {};
  const shotUrl = detail.payment_screenshot_url || null;

  const paymentLabel = () => {
    if (detail.payment_status === "completed" && detail.payment_approved) {
      return { text: "Payment approved", className: "bg-emerald-100 text-emerald-800" };
    }
    if (detail.payment_status === "failed") {
      return { text: "Payment declined", className: "bg-red-100 text-red-800" };
    }
    if (detail.payment_status === "pending" && detail.payment_screenshot) {
      return { text: "Awaiting admin verification", className: "bg-blue-100 text-blue-800" };
    }
    return {
      text: "Awaiting payment screenshot",
      className: "bg-amber-100 text-amber-900",
    };
  };

  const badge = paymentLabel();

  const drawings = Array.isArray(detail.selected_drawings)
    ? detail.selected_drawings
    : [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button
        type="button"
        onClick={() => navigate("/purchased-house-designs")}
        className="inline-flex items-center gap-2 text-primary-dark hover:underline mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to list
      </button>

      <div className="bg-gray-100 p-4 rounded-lg mb-6 flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Purchase #{detail.id}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Created{" "}
            {detail.created_at
              ? new Date(detail.created_at).toLocaleString()
              : "—"}
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${badge.className}`}
        >
          {badge.text}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
            Customer (account)
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Name</dt>
              <dd className="font-medium text-right">
                {user.firstName} {user.lastName}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Email</dt>
              <dd className="text-right break-all">{user.email || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Phone</dt>
              <dd className="text-right">{user.contactNo || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">City</dt>
              <dd className="text-right">{user.city || "—"}</dd>
            </div>
          </dl>
        </section>

        <section className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
            Billing / contact (at purchase)
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Name</dt>
              <dd className="font-medium text-right">{info.name || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Contact</dt>
              <dd className="text-right">{info.contactNo || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Address</dt>
              <dd className="text-right max-w-[70%]">{info.address || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">City</dt>
              <dd className="text-right">{info.city || "—"}</dd>
            </div>
          </dl>
        </section>

        <section className="bg-white rounded-xl shadow border border-gray-100 p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
            House design
          </h2>
          <h3 className="text-xl font-bold text-primary-dark mb-2">{hd.title || "—"}</h3>
          <div className="grid sm:grid-cols-2 gap-3 text-sm mb-4">
            <div>
              <span className="text-gray-500">Plot</span>{" "}
              <span className="font-medium">{hd.plot_dimension || "—"}</span>
            </div>
            <div>
              <span className="text-gray-500">Covered area</span>{" "}
              <span className="font-medium">{hd.covered_area || "—"}</span>
            </div>
            <div>
              <span className="text-gray-500">Floors</span>{" "}
              <span className="font-medium">{hd.floors ?? "—"}</span>
            </div>
            <div>
              <span className="text-gray-500">Beds / baths</span>{" "}
              <span className="font-medium">
                {hd.bed_rooms ?? "—"} / {hd.bath_rooms ?? "—"}
              </span>
            </div>
            {hd.customUrl && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">URL slug</span>{" "}
                <span className="font-mono text-xs">{hd.customUrl}</span>
              </div>
            )}
          </div>
          {hd.description && (
            <div
              className="prose prose-sm max-w-none text-gray-700 border-t pt-4"
              dangerouslySetInnerHTML={{ __html: hd.description }}
            />
          )}
        </section>

        <section className="bg-white rounded-xl shadow border border-gray-100 p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
            Order details
          </h2>
          <dl className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Total price</dt>
              <dd className="font-semibold text-lg">{formatMoney(detail.price)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Design label</dt>
              <dd className="break-words">{detail.design_id || "—"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Garage</dt>
              <dd className="capitalize">{detail.garage_location || "—"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Payment status</dt>
              <dd className="capitalize">{detail.payment_status || "—"}</dd>
            </div>
          </dl>

          {drawings.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-2">Selected drawings</h3>
              <ul className="divide-y rounded-lg border border-gray-200">
                {drawings.map((d, i) => (
                  <li
                    key={i}
                    className="flex flex-wrap justify-between gap-2 px-3 py-2 text-sm"
                  >
                    <span>{d.selectedDrawings || d.label || "Item"}</span>
                    <span className="font-medium">
                      {formatMoney(d.price)}
                      {d.buyPlanId != null && (
                        <span className="text-gray-400 ml-2">(plan #{d.buyPlanId})</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {shotUrl && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-2">Payment screenshot</h3>
              <a
                href={shotUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                Open image in new tab
              </a>
              <div className="mt-3 rounded-lg overflow-hidden border max-w-md">
                <img src={shotUrl} alt="Payment" className="w-full h-auto object-contain max-h-80 bg-gray-50" />
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
